import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import logger from '../utils/logger';

interface CustomSelectProps {
  label?: string;
  error?: string;
  options: { value: string; label: string }[];
  name?: string;
  value?: string;
  onChange?: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  className?: string;
  placeholder?: string;
}

const CustomSelect: React.FC<CustomSelectProps> = ({
  label,
  error,
  options,
  className = '',
  onChange,
  name,
  value = '',
  placeholder = 'Select an option'
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedLabel, setSelectedLabel] = useState('');
  const [openUpward, setOpenUpward] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const selectedOption = options.find(opt => opt.value === value);
    setSelectedLabel(selectedOption ? selectedOption.label : placeholder);
  }, [value, options, placeholder]);

  useEffect(() => {
    logger.debug('CustomSelect', 'CustomSelect rendered', { name, label, optionsCount: options.length, hasError: !!error, currentValue: value, isOpen });
  }, [name, label, options.length, error, value, isOpen]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: Event) => {
      const target = event.target as Node;
      if (
        dropdownRef.current &&
        buttonRef.current &&
        !dropdownRef.current.contains(target) &&
        !buttonRef.current.contains(target)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isOpen]);

  const handleToggle = () => {
    logger.userAction('CustomSelect', 'Dropdown toggled', { name, wasOpen: isOpen });

    if (!isOpen && buttonRef.current) {
      // Calculate position and direction
      const buttonRect = buttonRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const spaceBelow = viewportHeight - buttonRect.bottom;
      const spaceAbove = buttonRect.top;
      const dropdownHeight = Math.min(300, options.length * 48 + 16); // Approximate height

      // Open upward if not enough space below but more space above
      const shouldOpenUpward = spaceBelow < dropdownHeight && spaceAbove > spaceBelow;
      setOpenUpward(shouldOpenUpward);

      // Set dropdown position for portal
      setDropdownPosition({
        top: shouldOpenUpward ? buttonRect.top - 8 : buttonRect.bottom + 8,
        left: buttonRect.left,
        width: buttonRect.width
      });
    }

    setIsOpen(!isOpen);
  };

  const handleSelect = (optionValue: string) => {
    logger.userAction('CustomSelect', 'Option selected', { name, oldValue: value, newValue: optionValue });

    // Create a synthetic event to match the expected onChange signature
    const syntheticEvent = {
      target: {
        name: name || '',
        value: optionValue
      }
    } as React.ChangeEvent<HTMLSelectElement>;

    if (onChange) {
      onChange(syntheticEvent);
    }

    setIsOpen(false);
  };

  const selectedOption = options.find(opt => opt.value === value);

  return (
    <div className={`w-full relative ${className}`} style={{ zIndex: isOpen ? 1000 : 1 }}>
      {label && (
        <label className="block text-sm font-medium text-text-secondary mb-2 font-elegant tracking-wide uppercase text-xs opacity-80">
          {label}
        </label>
      )}
      <div className="relative">
        <button
          ref={buttonRef}
          type="button"
          onClick={handleToggle}
          className={`
            w-full px-4 py-3.5 rounded-2xl
            backdrop-blur-xl transition-all duration-300
            text-left flex items-center justify-between
            font-elegant text-base tracking-wide
            border
            ${error
              ? 'bg-red-500/10 border-red-500/30 text-red-200'
              : isOpen
                ? 'bg-white/10 border-white/30 text-white shadow-[0_0_20px_rgba(255,255,255,0.1)]'
                : 'bg-white/5 border-white/10 text-white/90 hover:bg-white/10 hover:border-white/20'
            }
          `}
        >
          <span className={`${!selectedOption ? 'text-white/40' : ''} truncate`}>
            {selectedLabel}
          </span>
          <svg
            className={`w-4 h-4 transition-transform duration-500 ease-out opacity-60 ${isOpen ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {isOpen && createPortal(
          <div
            ref={dropdownRef}
            className="fixed rounded-2xl overflow-hidden animate-fade-in-up origin-top"
            style={{
              top: openUpward ? 'auto' : `${dropdownPosition.top}px`,
              bottom: openUpward ? `${window.innerHeight - dropdownPosition.top}px` : 'auto',
              left: `${dropdownPosition.left}px`,
              width: `${dropdownPosition.width}px`,
              zIndex: 9999,
              backgroundColor: 'rgba(15, 15, 20, 0.95)',
              backdropFilter: 'blur(24px) saturate(180%)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              boxShadow: '0 20px 40px -10px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.05)',
              maxHeight: '300px',
              overflowY: 'auto'
            }}
          >
            <div className="p-1.5 space-y-0.5">
              {options.map((option) => {
                const isSelected = option.value === value;
                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => handleSelect(option.value)}
                    className={`
                      w-full px-4 py-2.5 text-left rounded-xl
                      font-elegant text-base tracking-wide
                      transition-all duration-200
                      flex items-center justify-between
                      group
                      ${isSelected
                        ? 'bg-white/10 text-white'
                        : 'text-white/60 hover:bg-white/5 hover:text-white'
                      }
                    `}
                  >
                    <span className="truncate">{option.label}</span>
                    {isSelected && (
                      <div className="w-1.5 h-1.5 rounded-full bg-cosmic-gold shadow-[0_0_8px_rgba(255,184,79,0.6)]" />
                    )}
                  </button>
                );
              })}
            </div>
          </div>,
          document.body
        )}
      </div>
      {error && (
        <p className="mt-1.5 text-xs text-red-400 font-elegant tracking-wide ml-1">{error}</p>
      )}
    </div>
  );
};

export default CustomSelect;

